import { useQuery } from "@apollo/client";
import { Navigate, Route, Routes, useParams } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";

import { ProviderContext } from "./Context";
import ProviderOverview from "./ProviderOverview";
import ProviderVersionDocs from "./ProviderVersionDocs";
import { FILTERS_ORDER_SETTINGS_KEY_PROVIDERS } from "./constants";
import { GET_PROVIDER_WITH_VERSIONS } from "./gql";

const Provider = () => {
  const { providerId } = useParams<{ providerId: string }>();

  const { error, loading, data } = useQuery(
    GET_PROVIDER_WITH_VERSIONS,
    {
      variables: {
        id: providerId!,
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-only",
      skip: !providerId,
    }
    // APOLLO CLIENT UPDATE
  );

  const provider = data?.terraformProvider;

  useBreadcrumbs(
    [
      {
        title: "Providers",
        link: getBreadcrumbsBackUrl("/providers", FILTERS_ORDER_SETTINGS_KEY_PROVIDERS),
      },
      {
        title: provider?.id || "",
      },
    ],
    [provider?.id]
  );

  useTitle(`Provider · ${provider?.id}`);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !provider?.id) {
    return <PageLoading />;
  }

  if (!provider?.id) {
    return <NotFoundPage />;
  }

  const providerContext = {
    provider,
  };

  return (
    <ProviderContext.Provider value={providerContext}>
      <Routes>
        <Route index element={<ProviderOverview provider={provider} loading={loading} />} />
        <Route path="version/:versionNumber" element={<Navigate to="docs" replace />} />
        <Route path="version/:versionNumber">
          <Route index path="*" element={<ProviderVersionDocs />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ProviderContext.Provider>
  );
};

export default Provider;
